/**
 * ChatPanel — UI reutilizável para uma thread de chat (admin ↔ mobile).
 *
 * Props:
 *  - threadId       (string)   — id da thread (chat_threads._id)
 *  - currentUserId  (string)   — id do usuário web (pra detectar "minhas" msgs)
 *  - headerSlot     (ReactNode opcional) — extra acima da lista (ex: nome do prestador)
 *  - className      (string opcional)
 *  - emptyHint      (string opcional) — mensagem inicial quando vazia
 *
 * Comportamento:
 *  - Carrega histórico inicial
 *  - Subscribe WS chat:message (filtra pela thread_id)
 *  - Marca como lida quando montado
 *  - Auto-scroll pro final em nova mensagem
 *  - Composer: textarea + Enter envia (Shift+Enter quebra linha)
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { compressImageForUpload } from "../utils/imageCompress";
import {
  Send,
  CheckCheck,
  PlayCircle,
  XCircle,
  Paperclip,
  Mic,
  Trash2,
  Loader2,
  Play,
  Pause,
  AtSign,
  MessageCircle,
  Reply,
  Pin,
  PinOff,
  Search,
  X,
  ChevronUp,
  ChevronDown,
} from "lucide-react";
import api, { formatApiError } from "../utils/api";
import { subscribeAdminWS } from "../lib/adminWS";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import QuickMessagesButton from "./QuickMessagesButton";
import { toast } from "./ui/sonner";
import { parseSystemMessage } from "./chat/messageIcons";

// Mensagem fixada: destaque laranja no balão.
const PINNED_BG = "#FF9935";


function formatTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}

function formatDay(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);
    const isSame = (a, b) => a.toDateString() === b.toDateString();
    if (isSame(d, today)) return "Hoje";
    if (isSame(d, yesterday)) return "Ontem";
    return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "short" });
  } catch {
    return "";
  }
}

// Mapa emoji-prefixo → ícone Lucide: compartilhado com a lista de conversas.

export default function ChatPanel({
  threadId,
  currentUserId,
  headerSlot,
  className = "",
  emptyHint,
  readonly,
  readonlyHint,
  // Chat interno (web ↔ web) reusa este painel com outra base e outro evento WS.
  apiBase = "/chat/threads",
  wsEvent = "chat:message",
  mentionUsers = null,
  currentUserName = "",
  onRead = null,
  // Quando informado, mostra o atalho de mensagens prontas da operação.
  quickMessagesServiceId = null,
}) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recordingMs, setRecordingMs] = useState(0);
  const scrollerRef = useRef(null);
  const composerRef = useRef(null);
  const fileInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordTimerRef = useRef(null);
  // Quando o usuário aperta "Cancelar", marcamos esse ref. O handler `onstop`
  // do MediaRecorder consulta isso e descarta o blob em vez de fazer upload.
  const recordCancelledRef = useRef(false);
  // Menções (@) no chat interno: ids escolhidos ficam aqui até o envio.
  const pendingMentions = useRef([]);
  const [mentionQuery, setMentionQuery] = useState(null);
  // Responder/citar mensagem
  const [replyTo, setReplyTo] = useState(null);
  // Mensagens fixadas na conversa (estilo WhatsApp, até 3)
  const [pinned, setPinned] = useState([]);
  const [pinIndex, setPinIndex] = useState(0);

  const loadPinned = useCallback(async () => {
    if (!threadId) return;
    try {
      const { data } = await api.get(`${apiBase}/${threadId}/pinned`);
      setPinned(data?.items || []);
    } catch {
      setPinned([]);
    }
  }, [threadId, apiBase]);

  const togglePin = useCallback(
    async (messageId, pin) => {
      try {
        await api.post(`${apiBase}/${threadId}/messages/${messageId}/pin`, {
          pinned: pin,
        });
        setMessages((prev) =>
          prev.map((m) =>
            m.id === messageId ? { ...m, pinned_at: pin ? new Date().toISOString() : null } : m,
          ),
        );
        setPinIndex(0);
        loadPinned();
        toast.success(pin ? "Mensagem fixada" : "Mensagem desafixada");
      } catch (err) {
        toast.error("Não foi possível fixar a mensagem", {
          description: formatApiError(err.response?.data?.detail),
        });
      }
    },
    [apiBase, threadId, loadPinned],
  );

  const [msgSearch, setMsgSearch] = useState("");
  const [hitIndex, setHitIndex] = useState(0);
  // Rascunho por conversa (localStorage) — o texto volta ao reabrir
  const draftThread = useRef(null);

  // Carrega histórico inicial
  const loadMessages = useCallback(async () => {
    if (!threadId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`${apiBase}/${threadId}/messages?limit=100`);
      setMessages(data?.items || []);
    } catch (err) {
      toast.error("Erro ao carregar mensagens", {
        description: formatApiError(err.response?.data?.detail, "Tente novamente."),
      });
    } finally {
      setLoading(false);
    }
  }, [threadId, apiBase]);

  // Marca como lida (chamado no mount + em novas msgs vindas pelo WS)
  const onReadRef = useRef(onRead);
  onReadRef.current = onRead;
  const markRead = useCallback(async () => {
    if (!threadId) return;
    try {
      await api.post(`${apiBase}/${threadId}/read`);
      onReadRef.current?.();
    } catch {
      /* silencioso */
    }
  }, [threadId, apiBase]);

  useEffect(() => {
    if (!threadId) return;
    draftThread.current = threadId;
    setText(localStorage.getItem(`chat_draft_${threadId}`) || "");
    setReplyTo(null);
    setMsgSearch("");
    setPinIndex(0);
    loadPinned();
    loadMessages().then(() => markRead());
  }, [threadId, loadMessages, markRead, loadPinned]);

  // Auto-scroll ao final
  useEffect(() => {
    const el = scrollerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length]);

  // WS: recebe novas mensagens
  useEffect(() => {
    if (!threadId) return;
    const unsub = subscribeAdminWS(wsEvent, (data) => {
      if (data?.thread_id !== threadId) return;
      const m = data.message;
      if (!m) return;
      setMessages((prev) => {
        if (prev.some((x) => x.id === m.id)) return prev;
        return [...prev, m];
      });
      // marca como lido se o painel está aberto
      markRead();
    });
    return unsub;
  }, [threadId, markRead, wsEvent]);

  const send = useCallback(async (opts = {}) => {
    const body = (opts.body !== undefined ? opts.body : text).trim();
    const attachments = opts.attachments || null;
    if ((!body && !attachments) || sending) return;
    setSending(true);
    try {
      const payload = { body };
      if (attachments) payload.attachments = attachments;
      if (mentionUsers && pendingMentions.current.length) {
        payload.mentions = [...new Set(pendingMentions.current)];
      }
      if (replyTo?.id) payload.reply_to_id = replyTo.id;
      const { data: msg } = await api.post(`${apiBase}/${threadId}/messages`, payload);
      pendingMentions.current = [];
      setReplyTo(null);
      setMessages((prev) => (prev.some((x) => x.id === msg.id) ? prev : [...prev, msg]));
      if (opts.body === undefined) setText("");
      localStorage.removeItem(`chat_draft_${threadId}`);
      composerRef.current?.focus();
    } catch (err) {
      toast.error("Erro ao enviar", {
        description: formatApiError(err.response?.data?.detail, "Tente novamente."),
      });
    } finally {
      setSending(false);
    }
  }, [text, threadId, sending, apiBase, mentionUsers, replyTo]);

  // Upload de foto/vídeo/arquivo via input file
  const handleFileSelected = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = ""; // permite escolher o mesmo arquivo de novo
    setUploading(true);
    try {
      // NÍVEL 1 — compressão antes do upload reduz banda/storage/tempo de
      // resposta. Imagens >600kB são redimensionadas pra max 1920×1080 + JPEG q80.
      const compressed = await compressImageForUpload(file);
      const fd = new FormData();
      fd.append("file", compressed);
      fd.append("thread_id", threadId);
      const { data: att } = await api.post("/chat/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await send({ body: "", attachments: [att] });
    } catch (err) {
      toast.error("Erro no upload", { description: formatApiError(err.response?.data?.detail, "Verifique S3 e tamanho do arquivo.") });
    } finally {
      setUploading(false);
    }
  }, [threadId, send]);

  // Gravação de áudio via MediaRecorder
  const startRecording = useCallback(async () => {
    if (recording || uploading) return;

    // Verifica se a API existe (HTTP local não suporta getUserMedia)
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      toast.error("Recurso indisponível", {
        description: "Seu navegador não suporta gravação de áudio nesta página. Tente Chrome/Edge atualizados em HTTPS.",
      });
      return;
    }

    // Checa estado da permissão (quando disponível) — se já estiver "denied",
    // navegador NÃO mostra prompt novamente; usuário precisa liberar manualmente.
    try {
      // @ts-ignore — Permissions API
      const status = await navigator.permissions?.query?.({ name: "microphone" });
      if (status?.state === "denied") {
        toast.error("Microfone bloqueado pelo navegador", {
          description:
            "Clique no ícone de cadeado/câmera na barra de endereço, libere o microfone e recarregue a página.",
          duration: 10000,
        });
        return;
      }
    } catch {
      /* Permissions API pode não existir em alguns browsers — segue tentando */
    }

    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      const name = err?.name || "Erro";
      if (name === "NotAllowedError" || name === "SecurityError") {
        toast.error("Permissão negada", {
          description:
            "Você bloqueou o microfone. Clique no ícone de cadeado na barra de endereço, libere o microfone e recarregue.",
          duration: 10000,
        });
      } else if (name === "NotFoundError" || name === "DevicesNotFoundError") {
        toast.error("Microfone não encontrado", {
          description: "Conecte um microfone ou verifique a configuração de áudio do seu sistema.",
        });
      } else if (name === "NotReadableError") {
        toast.error("Microfone em uso", {
          description: "Outro programa está usando o microfone. Feche-o e tente novamente.",
        });
      } else {
        toast.error("Erro ao acessar microfone", { description: err?.message || name });
      }
      return;
    }

    try {
      // Seleciona o melhor formato compatível com TODAS as plataformas.
      // iOS NÃO decoda audio/webm (codec Opus) — usa apenas containers MPEG-4 (AAC).
      // Chrome 116+ e Edge suportam gravação em audio/mp4. Safari também.
      // Caímos para webm como fallback (Firefox antigo, alguns Chromium velhos).
      const formatCandidates = [
        { mime: "audio/mp4;codecs=mp4a.40.2", ext: "m4a" },
        { mime: "audio/mp4", ext: "m4a" },
        { mime: "audio/webm;codecs=opus", ext: "webm" },
        { mime: "audio/webm", ext: "webm" },
      ];
      const pick = formatCandidates.find((c) => MediaRecorder.isTypeSupported(c.mime))
        || { mime: "audio/webm", ext: "webm" };
      const chosenMime = pick.mime;
      const chosenExt = pick.ext;

      const mr = new MediaRecorder(stream, { mimeType: chosenMime });
      audioChunksRef.current = [];
      recordCancelledRef.current = false;
      mr.ondataavailable = (ev) => { if (ev.data.size > 0) audioChunksRef.current.push(ev.data); };
      mr.onstop = async () => {
        const blob = new Blob(audioChunksRef.current, { type: chosenMime });
        audioChunksRef.current = [];
        stream.getTracks().forEach((t) => t.stop());
        // Se o usuário apertou "Cancelar", descarta sem enviar
        if (recordCancelledRef.current) {
          recordCancelledRef.current = false;
          return;
        }
        if (blob.size < 200) return;
        setUploading(true);
        try {
          const fd = new FormData();
          fd.append("file", new File([blob], `audio-${Date.now()}.${chosenExt}`, { type: chosenMime }));
          fd.append("thread_id", threadId);
          const { data: att } = await api.post("/chat/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
          await send({ body: "", attachments: [att] });
        } catch (err) {
          toast.error("Erro ao enviar áudio", { description: formatApiError(err.response?.data?.detail) });
        } finally {
          setUploading(false);
        }
      };
      mr.start();
      mediaRecorderRef.current = mr;
      setRecording(true);
      setRecordingMs(0);
      // Timer do contador da gravação
      const startedAt = Date.now();
      if (recordTimerRef.current) clearInterval(recordTimerRef.current);
      recordTimerRef.current = setInterval(() => {
        setRecordingMs(Date.now() - startedAt);
      }, 100);
    } catch (err) {
      stream?.getTracks?.().forEach((t) => t.stop());
      toast.error("Erro ao iniciar gravação", { description: err?.message || "Tente novamente." });
    }
  }, [recording, uploading, threadId, send]);

  // Envia a gravação atual (caminho feliz). O onstop do MediaRecorder cuida do upload.
  const stopRecording = useCallback(() => {
    const mr = mediaRecorderRef.current;
    recordCancelledRef.current = false;
    if (mr && mr.state !== "inactive") {
      mr.stop();
    }
    setRecording(false);
    if (recordTimerRef.current) {
      clearInterval(recordTimerRef.current);
      recordTimerRef.current = null;
    }
    setRecordingMs(0);
  }, []);

  // Cancela a gravação SEM enviar (descarta o blob no onstop).
  const cancelRecording = useCallback(() => {
    const mr = mediaRecorderRef.current;
    recordCancelledRef.current = true;
    if (mr && mr.state !== "inactive") {
      mr.stop();
    }
    setRecording(false);
    if (recordTimerRef.current) {
      clearInterval(recordTimerRef.current);
      recordTimerRef.current = null;
    }
    setRecordingMs(0);
  }, []);

  // Composer: detecta "@" para abrir a lista de menções (só no chat interno).
  const onComposerChange = (e) => {
    const v = e.target.value;
    setText(v);
    if (threadId && draftThread.current === threadId) {
      if (v.trim()) localStorage.setItem(`chat_draft_${threadId}`, v);
      else localStorage.removeItem(`chat_draft_${threadId}`);
    }
    if (!mentionUsers) return;
    const m = /(?:^|\s)@([\p{L}\s]{0,20})$/u.exec(v);
    setMentionQuery(m ? m[1] : null);
  };

  const pickMention = (u) => {
    setText((prev) => prev.replace(/(?:^|\s)@([\p{L}\s]{0,20})$/u, (all) => {
      const prefix = all.startsWith("@") ? "" : all[0];
      return `${prefix}@${u.name} `;
    }));
    pendingMentions.current = [...pendingMentions.current, u.id];
    setMentionQuery(null);
    composerRef.current?.focus();
  };

  const onKeyDown = (e) => {
    if (e.key === "Escape" && mentionQuery !== null) {
      setMentionQuery(null);
      return;
    }
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      setMentionQuery(null);
      send();
    }
  };

  // Agrupa por dia
  const grouped = useMemo(() => {
    const out = [];
    let lastDay = null;
    for (const m of messages) {
      const day = formatDay(m.created_at);
      if (day !== lastDay) {
        out.push({ kind: "day", id: `day-${day}-${m.id}`, label: day });
        lastDay = day;
      }
      out.push({ kind: "msg", ...m });
    }
    return out;
  }, [messages]);

  // Nomes conhecidos para destacar "@Nome" dentro do texto das mensagens.
  const mentionRegex = useMemo(() => {
    if (!mentionUsers) return null;
    const names = [...mentionUsers.map((u) => u.name), currentUserName]
      .filter(Boolean)
      .sort((a, b) => b.length - a.length)
      .map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
    if (!names.length) return null;
    return new RegExp(`(@(?:${names.join("|")}))`, "g");
  }, [mentionUsers, currentUserName]);

  const searchRegex = useMemo(() => {
    const q = msgSearch.trim();
    if (!q) return null;
    return new RegExp(`(${q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi");
  }, [msgSearch]);

  const renderBody = useCallback(
    (body, isMine) => {
      const parts = mentionRegex
        ? body.split(mentionRegex).map((part, i) =>
            i % 2 === 1 ? (
              <span
                key={`m${i}`}
                className={`font-semibold rounded px-0.5 ${isMine ? "bg-white/25" : ""}`}
                style={isMine ? undefined : { color: "var(--accent)" }}
              >
                {part}
              </span>
            ) : (
              part
            ),
          )
        : [body];
      if (!searchRegex) return parts;
      // Destaca o termo buscado nos pedaços de texto puro.
      return parts.map((part, i) =>
        typeof part === "string"
          ? part.split(searchRegex).map((chunk, j) =>
              j % 2 === 1 ? (
                <mark
                  key={`s${i}-${j}`}
                  className="bg-amber-300 text-gray-900 rounded px-0.5"
                >
                  {chunk}
                </mark>
              ) : (
                chunk
              ),
            )
          : part,
      );
    },
    [mentionRegex, searchRegex],
  );

  // Busca dentro da conversa: ids das mensagens que casam com o termo.
  const hits = useMemo(() => {
    const q = msgSearch.trim().toLowerCase();
    if (!q) return [];
    return messages.filter((m) => (m.body || "").toLowerCase().includes(q)).map((m) => m.id);
  }, [messages, msgSearch]);

  const hitSet = useMemo(() => new Set(hits), [hits]);
  const searching = !!msgSearch.trim();

  const jumpTo = useCallback((id, flash = true) => {
    const el = scrollerRef.current?.querySelector(`[data-msg-id="${id}"]`);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    if (!flash) return;
    el.classList.add("ring-2", "ring-[var(--accent)]", "rounded-2xl");
    setTimeout(
      () => el.classList.remove("ring-2", "ring-[var(--accent)]", "rounded-2xl"),
      1200,
    );
  }, []);

  useEffect(() => {
    setHitIndex(0);
  }, [msgSearch]);

  useEffect(() => {
    if (hits.length) jumpTo(hits[Math.min(hitIndex, hits.length - 1)], false);
  }, [hits, hitIndex, jumpTo]);
  const currentHit = hits.length ? hits[Math.min(hitIndex, hits.length - 1)] : null;

  return (
    <div className={`flex flex-col h-full bg-background ${className}`} data-testid="chat-panel">
      {headerSlot ? <div className="border-b border-border">{headerSlot}</div> : null}

      <div className="border-b border-border px-3 py-1.5 flex items-center gap-2 bg-background shrink-0">
        <Search size={13} className="text-gray-400 shrink-0" />
            <input
              value={msgSearch}
              onChange={(e) => setMsgSearch(e.target.value)}
              placeholder="Buscar nesta conversa…"
              className="flex-1 bg-transparent text-[12px] outline-none placeholder:text-gray-400"
              data-testid="chat-search-messages-input"
            />
            <span
              className="text-[11px] text-gray-400 tabular-nums shrink-0"
              data-testid="chat-search-count"
            >
              {msgSearch.trim()
                ? hits.length
                  ? `${Math.min(hitIndex, hits.length - 1) + 1} de ${hits.length}`
                  : "0 de 0"
                : ""}
            </span>
            <button
              type="button"
              disabled={hits.length < 2}
              onClick={() => setHitIndex((i) => (i - 1 + hits.length) % hits.length)}
              className="p-0.5 text-gray-400 hover:text-gray-700 disabled:opacity-30"
              title="Anterior"
              data-testid="chat-search-prev"
            >
              <ChevronUp size={14} />
            </button>
            <button
              type="button"
              disabled={hits.length < 2}
              onClick={() => setHitIndex((i) => (i + 1) % hits.length)}
              className="p-0.5 text-gray-400 hover:text-gray-700 disabled:opacity-30"
              title="Próxima"
              data-testid="chat-search-next"
            >
              <ChevronDown size={14} />
            </button>
            <button
              type="button"
              onClick={() => setMsgSearch("")}
              disabled={!msgSearch}
              className="p-0.5 text-gray-400 hover:text-gray-700 disabled:opacity-30"
              title="Limpar busca"
              data-testid="chat-search-close"
            >
              <X size={14} />
            </button>
      </div>

      {pinned.length > 0 && (
        <div
          className="border-b px-3 py-2 flex items-center gap-2.5 shrink-0"
          style={{ backgroundColor: PINNED_BG, borderColor: "rgba(0,0,0,0.08)" }}
          data-testid="chat-pinned-banner"
        >
          <div className="flex flex-col gap-[3px] shrink-0">
            {pinned.map((p, i) => (
              <span
                key={p.id}
                className={`w-[3px] h-[7px] rounded-full ${
                  i === pinIndex % pinned.length ? "bg-white" : "bg-white/50"
                }`}
              />
            ))}
          </div>
          <button
            type="button"
            onClick={() => {
              const current = pinned[pinIndex % pinned.length];
              jumpTo(current.id);
              if (pinned.length > 1) setPinIndex((i) => (i + 1) % pinned.length);
            }}
            className="flex-1 min-w-0 text-left"
            data-testid="chat-pinned-jump"
          >
            <span className="flex items-center gap-1 text-[10px] font-semibold text-white">
              <Pin size={9} strokeWidth={3} />
              Mensagem fixada
              {pinned.length > 1 && ` · ${(pinIndex % pinned.length) + 1}/${pinned.length}`}
            </span>
            <span className="block text-[12px] text-white/90 truncate">
              {pinned[pinIndex % pinned.length].sender_name}:{" "}
              {pinned[pinIndex % pinned.length].body || "📎 Anexo"}
            </span>
          </button>
          {!readonly && (
            <button
              type="button"
              onClick={() => togglePin(pinned[pinIndex % pinned.length].id, false)}
              className="p-1 rounded text-white hover:bg-white/20 shrink-0"
              title="Desafixar mensagem"
              data-testid="chat-pinned-unpin"
            >
              <PinOff size={14} />
            </button>
          )}
        </div>
      )}

      <div
        ref={scrollerRef}
        className="flex-1 overflow-y-auto px-4 py-4 space-y-2"
        data-testid="chat-messages"
      >
        {loading ? (
          <div className="text-sm text-muted-foreground text-center py-8">Carregando…</div>
        ) : grouped.length === 0 ? (
          <div
            className="h-full flex flex-col items-center justify-center text-gray-400 p-8"
            data-testid="chat-no-messages"
          >
            <span
              className="h-16 w-16 rounded-2xl flex items-center justify-center mb-4"
              style={{ backgroundColor: "var(--accent-light)" }}
            >
              <MessageCircle size={28} style={{ color: "var(--accent)" }} />
            </span>
            <p className="text-sm font-medium text-gray-500">Nenhuma mensagem ainda</p>
            <p className="text-xs text-gray-400 mt-1 text-center max-w-[280px]">
              {emptyHint || "Envie a primeira mensagem para começar a conversa."}
            </p>
          </div>
        ) : (
          grouped.map((row) => {
            if (row.kind === "day") {
              return (
                <div
                  key={row.id}
                  className={`flex justify-center my-3 transition-opacity ${
                    searching ? "opacity-25" : ""
                  }`}
                >
                  <span className="text-[11px] font-medium text-muted-foreground bg-muted px-3 py-1 rounded-full">
                    {row.label}
                  </span>
                </div>
              );
            }
            const m = row;
            if (m.is_system || m.sender_type === "system") {
              const sys = parseSystemMessage(m.body);
              const Icon = sys.Icon;
              return (
                <div
                  key={m.id}
                  className={`flex items-center gap-2 my-1.5 animate-in fade-in duration-300 transition-opacity ${
                    searching ? "opacity-25" : ""
                  }`}
                  data-testid={`chat-system-${m.id}`}
                >
                  <span className="flex-1 h-px bg-gray-100" />
                  <span className="flex items-center gap-1.5 shrink-0">
                    <Icon className="h-3 w-3 text-gray-300 shrink-0" />
                    <span className="text-[11px] text-gray-400 leading-tight">
                      {sys.text}
                      <span className="text-gray-300"> · {formatTime(m.created_at)}</span>
                    </span>
                  </span>
                  <span className="flex-1 h-px bg-gray-100" />
                </div>
              );
            }
            // "Da base" = qualquer mensagem enviada pelo painel web (sender_type
            // "admin"), de qualquer operador. "Minha" = enviada pelo usuário
            // logado. As duas seguem alinhadas à direita.
            const isFromBase = m.sender_type === "admin";
            const isMine = !!currentUserId && m.sender_id === currentUserId;
            const daBase = isFromBase || isMine;
            const mentionsMe =
              !daBase &&
              !!currentUserId &&
              Array.isArray(m.mentions) &&
              m.mentions.includes(currentUserId);
            return (
              <div
                key={m.id}
                className={`group flex items-center gap-1 ${daBase ? "justify-end" : "justify-start"} animate-in slide-in-from-bottom-2 fade-in duration-300`}
                data-testid={`chat-message-${m.id}`}
              >
                {daBase && !readonly && (
                  <span className="flex items-center opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                    <button
                      type="button"
                      onClick={() => togglePin(m.id, !m.pinned_at)}
                      className="p-1 text-gray-400 hover:text-amber-600"
                      title={m.pinned_at ? "Desafixar mensagem" : "Fixar mensagem"}
                      data-testid={`chat-pin-btn-${m.id}`}
                    >
                      {m.pinned_at ? <PinOff size={14} /> : <Pin size={14} />}
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        setReplyTo({
                          id: m.id,
                          sender_name: m.sender_name,
                          body: m.body || "📎 Anexo",
                        })
                      }
                      className="p-1 text-gray-400 hover:text-gray-700"
                      title="Responder"
                      data-testid={`chat-reply-btn-${m.id}`}
                    >
                      <Reply size={14} />
                    </button>
                  </span>
                )}
                <div
                  data-msg-id={m.id}
                  className={`max-w-[68%] rounded-2xl px-2.5 py-1.5 transition-opacity ${
                    daBase
                      ? "rounded-br-sm text-white"
                      : `text-foreground rounded-bl-sm ${mentionsMe ? "" : "bg-gray-100"}`
                  } ${
                    searching
                      ? hitSet.has(m.id)
                        ? currentHit === m.id
                          ? "shadow-md"
                          : ""
                        : "opacity-25"
                      : ""
                  }`}
                  data-testid={mentionsMe ? `chat-message-mention-${m.id}` : undefined}
                  style={
                    daBase
                      ? { backgroundColor: "var(--accent)" }
                      : mentionsMe
                        ? {
                            backgroundColor: "var(--accent-light)",
                            boxShadow: "inset 3px 0 0 0 var(--accent)",
                          }
                        : undefined
                  }
                >
                  {!daBase && (
                    <div className="text-[11px] font-semibold opacity-70 mb-0.5 leading-tight flex items-center gap-1">
                      {m.sender_name}
                      {m.pinned_at && <Pin size={9} style={{ color: PINNED_BG }} />}
                      {mentionsMe && (
                        <span
                          className="text-[9px] px-1 rounded-full font-bold text-white inline-flex items-center gap-0.5"
                          style={{ backgroundColor: "var(--accent)" }}
                        >
                          <AtSign size={7} strokeWidth={3} />
                          você
                        </span>
                      )}
                    </div>
                  )}
                  {m.reply_to && (
                    <button
                      type="button"
                      onClick={() => jumpTo(m.reply_to.id)}
                      className={`block w-full text-left mb-1 rounded-md px-2 py-1 border-l-2 ${
                        daBase
                          ? "bg-white/15 border-white/60"
                          : "bg-black/[0.05] border-[var(--accent)]"
                      }`}
                      data-testid={`chat-quote-${m.id}`}
                    >
                      <span className="block text-[10px] font-semibold truncate">
                        {m.reply_to.sender_name || "Mensagem"}
                      </span>
                      <span className="block text-[11px] opacity-80 truncate">
                        {m.reply_to.body}
                      </span>
                    </button>
                  )}
                  {Array.isArray(m.attachments) && m.attachments.length > 0 && (
                    <div className="space-y-1 mb-0.5">
                      {m.attachments.map((att) => (
                        <ChatAttachment key={att.id || att.key} attachment={att} isMine={daBase} />
                      ))}
                    </div>
                  )}
                  {/* Hora inline ao lado do texto, estilo WhatsApp (mensagens do
                      prestador). Nas mensagens da base o rodapé ganha o nome de
                      quem enviou, então vira uma linha própria. */}
                  {m.body ? (
                    <div className="text-[13px] leading-snug whitespace-pre-wrap break-words">
                      {renderBody(m.body, daBase)}
                      {!daBase && <span className="inline-block w-[52px]" aria-hidden />}
                    </div>
                  ) : null}
                  <div
                    className={`text-[10px] text-right flex items-center justify-end gap-0.5 leading-none ${
                      daBase ? "opacity-70 mt-1" : "opacity-60 -mt-3"
                    }`}
                  >
                    {daBase && (
                      <>
                        <span className="font-semibold truncate" data-testid={`chat-msg-sender-${m.id}`}>
                          {m.sender_name || "Base"}
                        </span>
                        <span aria-hidden>·</span>
                      </>
                    )}
                    <span>{formatTime(m.created_at)}</span>
                    {daBase && (
                      m.read_at_mobile ? (
                        <CheckCheck className="h-3 w-3" style={{ color: "#0EA5E9" }} />
                      ) : (
                        <CheckCheck className="h-3 w-3 opacity-60" />
                      )
                    )}
                  </div>
                </div>
                {!daBase && !readonly && (
                  <span className="flex items-center opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                    <button
                      type="button"
                      onClick={() =>
                        setReplyTo({
                          id: m.id,
                          sender_name: m.sender_name,
                          body: m.body || "📎 Anexo",
                        })
                      }
                      className="p-1 text-gray-400 hover:text-gray-700"
                      title="Responder"
                      data-testid={`chat-reply-btn-${m.id}`}
                    >
                      <Reply size={14} />
                    </button>
                    <button
                      type="button"
                      onClick={() => togglePin(m.id, !m.pinned_at)}
                      className="p-1 text-gray-400 hover:text-amber-600"
                      title={m.pinned_at ? "Desafixar mensagem" : "Fixar mensagem"}
                      data-testid={`chat-pin-btn-${m.id}`}
                    >
                      {m.pinned_at ? <PinOff size={14} /> : <Pin size={14} />}
                    </button>
                  </span>
                )}
              </div>
            );
          })
        )}
      </div>

      {!readonly && !recording && uploading && (
        <div className="border-t border-border/60 px-3 pt-2 bg-background">
          <div
            className="mb-2 flex items-center gap-2 text-[11px] text-gray-500"
            data-testid="chat-upload-progress"
          >
            <span className="h-1.5 flex-1 rounded-full bg-gray-100 overflow-hidden">
              <span
                className="block h-full w-1/2 animate-pulse rounded-full"
                style={{ backgroundColor: "var(--accent)" }}
              />
            </span>
            Enviando anexo…
          </div>
        </div>
      )}

      {!readonly && replyTo && (
        <div
          className="border-t border-border/60 px-3 pt-2 pb-2 bg-background"
          data-testid="chat-reply-preview"
        >
          <div
            className="flex items-start gap-2 rounded-md px-2.5 py-1.5 border-l-2"
            style={{
              backgroundColor: "var(--accent-light)",
              borderLeftColor: "var(--accent)",
            }}
          >
            <Reply size={13} className="mt-0.5 shrink-0" style={{ color: "var(--accent)" }} />
            <div className="flex-1 min-w-0">
              <p className="text-[11px] font-semibold text-gray-700 truncate">
                Respondendo {replyTo.sender_name || ""}
              </p>
              <p className="text-[11px] text-gray-500 truncate">{replyTo.body}</p>
            </div>
            <button
              type="button"
              onClick={() => setReplyTo(null)}
              className="p-0.5 text-gray-400 hover:text-gray-700 shrink-0"
              title="Cancelar resposta"
              data-testid="chat-reply-cancel"
            >
              <X size={14} />
            </button>
          </div>
        </div>
      )}

      <div className="border-t border-border p-3 flex items-end gap-2 bg-background">
        {readonly ? (
          <div className="flex-1 py-2 px-3 rounded-md bg-muted text-xs text-muted-foreground text-center whitespace-pre-line">
            {readonlyHint || "Esta conversa foi encerrada. Você pode ler o histórico, mas não enviar novas mensagens."}
          </div>
        ) : recording ? (
          // ── MODO GRAVAÇÃO ──────────────────────────────────────────────
          // Substitui todo o composer por uma barra dedicada com cancelar/enviar.
          // O usuário NUNCA fica sem saída: pode descartar via Trash2 OU enviar
          // via paper-plane. Removemos a confusão do antigo "press-and-hold".
          <div className="flex-1 flex items-center gap-2" data-testid="chat-recording-bar">
            <button
              type="button"
              onClick={cancelRecording}
              className="h-10 w-10 shrink-0 rounded-full bg-red-100 hover:bg-red-200 text-red-600 flex items-center justify-center transition-colors"
              title="Cancelar gravação (descarta)"
              aria-label="Cancelar gravação"
              data-testid="chat-record-cancel-btn"
            >
              <Trash2 className="h-4 w-4" />
            </button>
            <div
              className="flex-1 flex items-center gap-3 rounded-md px-3 py-2 bg-red-50 border border-red-200"
            >
              <span
                className="inline-block h-2.5 w-2.5 rounded-full bg-red-600 animate-pulse"
                aria-hidden
              />
              <span className="text-sm font-semibold text-red-700">Gravando...</span>
              <span className="ml-auto text-sm font-mono tabular-nums text-red-700">
                {(() => {
                  const total = Math.floor(recordingMs / 1000);
                  const mm = String(Math.floor(total / 60)).padStart(2, "0");
                  const ss = String(total % 60).padStart(2, "0");
                  return `${mm}:${ss}`;
                })()}
              </span>
            </div>
            <Button
              type="button"
              onClick={stopRecording}
              size="icon"
              className="shrink-0 text-white hover:opacity-90"
              style={{ backgroundColor: "var(--accent)" }}
              title="Enviar áudio"
              aria-label="Enviar áudio"
              data-testid="chat-record-send-btn"
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,video/*,audio/*"
              onChange={handleFileSelected}
              className="hidden"
              data-testid="chat-file-input"
            />
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="shrink-0"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading || recording}
              title="Anexar foto, vídeo ou arquivo"
              data-testid="chat-attach-btn"
            >
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
            </Button>
            <div className="relative flex-1">
              {mentionUsers && mentionQuery !== null && (
                <div
                  className="absolute bottom-full mb-2 left-0 w-full max-h-48 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg z-20"
                  data-testid="chat-mention-list"
                >
                  {(mentionUsers || [])
                    .filter((u) =>
                      u.name.toLowerCase().includes((mentionQuery || "").toLowerCase()),
                    )
                    .slice(0, 8)
                    .map((u) => (
                      <button
                        key={u.id}
                        type="button"
                        onClick={() => pickMention(u)}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center gap-2"
                        data-testid={`chat-mention-${u.id}`}
                      >
                        <span className="h-6 w-6 rounded-full bg-[var(--accent)] text-white text-[10px] font-semibold flex items-center justify-center">
                          {u.name.slice(0, 1).toUpperCase()}
                        </span>
                        {u.name}
                      </button>
                    ))}
                  {(mentionUsers || []).filter((u) =>
                    u.name.toLowerCase().includes((mentionQuery || "").toLowerCase()),
                  ).length === 0 && (
                    <p className="px-3 py-2 text-xs text-gray-400">Ninguém encontrado</p>
                  )}
                </div>
              )}
              <Textarea
                ref={composerRef}
                value={text}
                onChange={onComposerChange}
                onKeyDown={onKeyDown}
                placeholder={mentionUsers ? "Escreva uma mensagem… use @ para marcar alguém" : "Escreva uma mensagem..."}
                rows={1}
                className="resize-none min-h-[40px] max-h-[140px] w-full"
                data-testid="chat-composer-input"
              />
            </div>
            {quickMessagesServiceId && (
              <QuickMessagesButton
                serviceId={quickMessagesServiceId}
                disabled={sending || uploading || recording}
                onPick={(msg) => send({ body: msg })}
              />
            )}
            {text.trim() ? (
              <Button
                onClick={() => send()}
                disabled={!text.trim() || sending}
                size="icon"
                className="shrink-0 text-white hover:opacity-90 disabled:opacity-50"
                style={{ backgroundColor: "var(--accent)" }}
                data-testid="chat-send-btn"
                aria-label="Enviar"
              >
                <Send className="h-4 w-4" />
              </Button>
            ) : (
              <Button
                type="button"
                onClick={startRecording}
                disabled={uploading}
                size="icon"
                className="shrink-0 text-white hover:opacity-90"
                style={{ backgroundColor: "var(--accent)" }}
                data-testid="chat-mic-btn"
                aria-label="Gravar áudio"
                title="Gravar áudio (clique para iniciar)"
              >
                <Mic className="h-4 w-4" />
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Renderiza um anexo de chat (image / video / audio / file) com signed URL refresh.
 */
function ChatAttachment({ attachment, isMine }) {
  const [src, setSrc] = useState(attachment?.url || null);
  const [thumbSrc, setThumbSrc] = useState(attachment?.thumb_url || null);
  const [resolving, setResolving] = useState(false);
  const [lightbox, setLightbox] = useState(false);

  const refreshUrl = useCallback(async () => {
    if (!attachment?.key) return;
    setResolving(true);
    try {
      const { data } = await api.get(`/chat/attachments/sign?key=${encodeURIComponent(attachment.key)}`);
      setSrc(data.url);
    } finally {
      setResolving(false);
    }
  }, [attachment?.key]);

  const refreshThumbUrl = useCallback(async () => {
    if (!attachment?.thumb_key) return;
    try {
      const { data } = await api.get(`/chat/attachments/sign?key=${encodeURIComponent(attachment.thumb_key)}`);
      setThumbSrc(data.url);
    } catch (err) {
      console.warn("[chat] thumb sign failed:", err?.message);
    }
  }, [attachment?.thumb_key]);

  // Refaz signed URL se a inicial estiver ausente
  useEffect(() => {
    if (!src && attachment?.key) refreshUrl();
  }, [src, attachment?.key, refreshUrl]);

  // Mesmo pro thumb (assinatura expira em 24h — após isso histórico precisa re-assinar)
  useEffect(() => {
    if (!thumbSrc && attachment?.thumb_key) refreshThumbUrl();
  }, [thumbSrc, attachment?.thumb_key, refreshThumbUrl]);

  // A URL salva na mensagem é assinada e expira em 24h: no histórico o link
  // morre e a mídia quebra (ícone de imagem quebrada + nome do arquivo). Ao
  // falhar o carregamento, re-assina na hora — uma vez por anexo.
  const [reassinado, setReassinado] = useState(false);
  const onMediaError = useCallback(() => {
    if (reassinado || !attachment?.key) return;
    setReassinado(true);
    if (attachment?.thumb_key) refreshThumbUrl();
    refreshUrl();
  }, [reassinado, attachment?.key, attachment?.thumb_key, refreshThumbUrl, refreshUrl]);

  if (!attachment) return null;
  const kind = attachment.kind || "file";

  if (kind === "image") {
    // Listagem do chat: usa THUMB (320×320, ~50kB). Carregamento instantâneo.
    // Lightbox: usa ORIGINAL (`src`). Baixa só quando o usuário toca pra ampliar.
    const previewSrc = thumbSrc || src;
    return (
      <>
        <button
          type="button"
          onClick={() => src && setLightbox(true)}
          className="block max-w-full rounded-lg overflow-hidden bg-black/10 hover:opacity-95 transition-opacity"
        >
          {previewSrc ? (
            <img src={previewSrc} alt={attachment.filename || ""} onError={onMediaError} className="rounded-lg max-w-full max-h-80 object-cover cursor-zoom-in" />
          ) : (
            <div className="h-32 w-48 flex items-center justify-center text-xs">{resolving ? "Carregando..." : "Imagem"}</div>
          )}
        </button>
        {lightbox && src && (
          <MediaLightbox kind="image" src={src} filename={attachment.filename} onClose={() => setLightbox(false)} />
        )}
      </>
    );
  }
  if (kind === "video") {
    return (
      <>
        <button
          type="button"
          onClick={() => src && setLightbox(true)}
          className="relative block rounded-lg overflow-hidden bg-black hover:opacity-95"
        >
          {src ? (
            <>
              <video src={src} onError={onMediaError} className="rounded-lg max-w-full max-h-80" preload="metadata" />
              <span className="absolute inset-0 flex items-center justify-center bg-black/30">
                <PlayCircle className="h-12 w-12 text-white drop-shadow-md" />
              </span>
            </>
          ) : (
            <div className="h-32 w-48 flex items-center justify-center text-xs text-white">{resolving ? "Carregando..." : "Vídeo"}</div>
          )}
        </button>
        {lightbox && src && (
          <MediaLightbox kind="video" src={src} filename={attachment.filename} onClose={() => setLightbox(false)} />
        )}
      </>
    );
  }
  if (kind === "audio") {
    return <WebAudioPlayer key={src || "loading"} src={src} resolving={resolving} onError={onMediaError} isMine={isMine} />;
  }
  return (
    <a href={src || "#"} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs underline">
      <Paperclip className="h-3.5 w-3.5" />
      {attachment.filename || "Arquivo"}
    </a>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// WebAudioPlayer — player compacto estilo WhatsApp (botão circular + waveform
// pseudo-aleatório + tempo). Substitui o <audio controls> nativo que é grande
// e tem visual inconsistente entre browsers.
// ─────────────────────────────────────────────────────────────────────────────
function WebAudioPlayer({ src, resolving, onError, isMine }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);
  const [ready, setReady] = useState(false);

  const onLoadedMeta = () => {
    const a = audioRef.current;
    if (!a) return;
    // Em alguns browsers (Chromium) `duration` pode ser Infinity até o áudio rodar.
    if (Number.isFinite(a.duration)) setDuration(a.duration);
    setReady(true);
  };
  const onTime = () => {
    const a = audioRef.current;
    if (!a) return;
    setCurrent(a.currentTime);
    if (!Number.isFinite(duration) || duration === 0) {
      if (Number.isFinite(a.duration) && a.duration > 0) setDuration(a.duration);
    }
  };
  const onEnded = () => { setPlaying(false); setCurrent(0); };

  const toggle = async () => {
    const a = audioRef.current;
    if (!a || !src) return;
    if (playing) {
      a.pause();
      setPlaying(false);
    } else {
      try {
        await a.play();
        setPlaying(true);
      } catch (e) {
        // autoplay bloqueado ou erro de carregamento
        console.warn("[audio] play failed:", e?.message);
      }
    }
  };

  // Waveform pseudo-aleatório determinístico a partir do src (estilo WhatsApp).
  const bars = useMemo(() => {
    const N = 32;
    const key = String(src || "");
    let seed = 0;
    for (let i = 0; i < key.length; i++) seed = (seed * 31 + key.charCodeAt(i)) | 0;
    const rng = () => {
      seed = (seed * 1664525 + 1013904223) | 0;
      return (seed >>> 0) / 0xffffffff;
    };
    const arr = [];
    for (let i = 0; i < N; i++) arr.push(0.25 + rng() * 0.75);
    return arr;
  }, [src]);

  const pct = duration > 0 ? Math.min(current / duration, 1) : 0;
  const fmt = (s) => {
    if (!Number.isFinite(s) || s < 0) s = 0;
    const total = Math.floor(s);
    const mm = String(Math.floor(total / 60)).padStart(2, "0");
    const ss = String(total % 60).padStart(2, "0");
    return `${mm}:${ss}`;
  };

  // Paleta adapta ao lado:
  // - isMine (bolha cinza #d6d6d6): player em tons neutros escuros, sem azul (acabamento sóbrio)
  // - !isMine (bolha branca): player com accent ciano discreto pra dar vida
  const bg = isMine ? "rgba(0,0,0,0.05)" : "#f1f5f9";
  const btnBg = isMine ? "#475569" : "var(--accent)";
  const btnIcon = "#ffffff";
  const barActive = isMine ? "#475569" : "var(--accent)";
  const barInactive = isMine ? "#9ca3af" : "#cbd5e1";
  const timeColor = isMine ? "#000000" : "#64748b";

  return (
    <div
      className="inline-flex items-center gap-2 rounded-lg"
      style={{ backgroundColor: bg, padding: "4px 8px 4px 4px", minWidth: 220, maxWidth: 260 }}
      data-testid="chat-audio-player"
    >
      <audio
        ref={audioRef}
        src={src || undefined}
        preload="metadata"
        onLoadedMetadata={onLoadedMeta}
        onDurationChange={onLoadedMeta}
        onTimeUpdate={onTime}
        onEnded={onEnded}
        onError={onError}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        style={{ display: "none" }}
      />
      <button
        type="button"
        onClick={toggle}
        disabled={!ready || !src}
        className="shrink-0 rounded-full flex items-center justify-center transition-opacity hover:opacity-90 disabled:opacity-50"
        style={{ width: 32, height: 32, backgroundColor: btnBg }}
        aria-label={playing ? "Pausar" : "Tocar"}
      >
        {resolving || !ready ? (
          <Loader2 className="h-4 w-4 animate-spin" style={{ color: btnIcon }} />
        ) : playing ? (
          <Pause className="h-4 w-4" style={{ color: btnIcon, fill: btnIcon }} />
        ) : (
          <Play className="h-4 w-4" style={{ color: btnIcon, fill: btnIcon, marginLeft: 1 }} />
        )}
      </button>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-[2px]" style={{ height: 18 }}>
          {bars.map((h, i) => {
            const reached = i / bars.length <= pct;
            return (
              <span
                key={i}
                style={{
                  flex: 1,
                  height: Math.max(3, h * 16),
                  borderRadius: 1.5,
                  backgroundColor: reached ? barActive : barInactive,
                }}
              />
            );
          })}
        </div>
        <div
          className="text-[10px] tabular-nums leading-none mt-1"
          style={{ color: timeColor }}
        >
          {fmt(current)}{duration > 0 ? ` / ${fmt(duration)}` : ""}
        </div>
      </div>
    </div>
  );
}

/**
 * Lightbox fullscreen pra visualizar imagens e vídeos com controles.
 */
function MediaLightbox({ kind, src, filename, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[200] bg-black/90 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="chat-media-lightbox"
    >
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        className="absolute top-4 right-4 h-10 w-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
        data-testid="lightbox-close"
        aria-label="Fechar"
      >
        <XCircle className="h-6 w-6" />
      </button>
      {filename && (
        <div className="absolute top-4 left-4 text-white/80 text-xs max-w-[60%] truncate">{filename}</div>
      )}
      <a
        href={src}
        target="_blank"
        rel="noreferrer"
        onClick={(e) => e.stopPropagation()}
        className="absolute bottom-4 right-4 text-white/80 hover:text-white text-xs underline"
      >
        Abrir em nova aba
      </a>
      <div onClick={(e) => e.stopPropagation()} className="max-w-[90vw] max-h-[90vh] flex items-center justify-center">
        {kind === "image" ? (
          <img src={src} alt={filename || ""} className="max-w-[90vw] max-h-[90vh] object-contain" />
        ) : (
          <video src={src} controls autoPlay className="max-w-[90vw] max-h-[90vh]" />
        )}
      </div>
    </div>
  );
}
